import { useCallback, useContext, useEffect, useRef, useState, useImperativeHandle } from "react";
import { FlowStateContextApi, TabContextApi } from "../../../../context";
import useUndoRedo from "hooks/useUndoRedo";
import ReactFlow, { addEdge, getConnectedEdges, getIncomers, getOutgoers, MiniMap, useEdgesState, useNodesInitialized, useNodesState, useOnSelectionChange, useReactFlow, useStoreApi } from "reactflow";
import { EDGE_TYPES, COMMON_COMPONENT_TYPES, CANVAS_DIRECTIONS, NODE_TYPES } from "constants/flowDesigner";
import { useDispatch, useSelector } from "react-redux";
import useCheckTriggersDuplicate from "hooks/useCheckTriggersDuplicate";
import { setFlowTriggers, addFlowTrigger, clearFlowTriggers } from "redux/actions/flowDesigner/triggersActionCreator";
import { addContextTrigger } from "redux/actions/flowDesigner/editorContextActionCreator";
import useComponentIdGenerator from "hooks/useComponentIdGenerator";
import useFlowStepConnection from "hooks/useFlowStepConnection";
import useLimitNumberOfNode from "hooks/useLimitNumberOfNode";
import { isEmptyValue, getSchemaUrlFromConfigFiles } from "helpers/common";
import { contextCleanUp, adjustTriggerCredentials, removeElementClassname } from "layouts/Flow Designer/helpers/common";
import useGetBranchNodes from "hooks/useGetBranchNodes";
import { setFlowIsLoading, setCanvasFlowInstance, setIsChangingFlowMode, fetchFlowConfiguration, fetchSharedFlowConfigurations } from "redux/actions/flowDesigner/flowsActionCreator";
import FlowControls from "../../components/FlowControls";
import ModeControl from "../../components/ModeControl";
import { getCredentialById } from "api/CredentialManagementApi";
import { openShareeFlowDialog, setCredentialsInSharedFlow } from "redux/actions/portal/flowsActionCreator";
import { updateContextVariable, setContext, removeStepFromContext, deleteContextVariable } from "redux/actions/flowDesigner/editorContextActionCreator";
import { getConnectorConfigFile } from "api/ExecutorServiceApi";
import store from "redux/store";
import useDetermineMissingConnectors from "hooks/useDetermineMissingConnectors";
import { useHandleFloatingEdge } from "hooks/useHandleFloatingEdge";
import { initialEdges, initialNodes } from "../..";
import { fetchFlowById } from "api/FlowDesignerApis";
import { CircularProgressWrapper } from "./progressWrapper";
import useAutoPanOnDrag from "hooks/useAutoPanOnDrag";
import useAutoLayout from "hooks/useAutoLayout";
import { useAuth0 } from "@auth0/auth0-react";
import { hideComponentPropsPanel, setRecentlyDeletedBranchNodeId } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { clearSelectedLogNodeId } from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { nodeTypes, edgeTypes, defaultEdgeOptions } from "../FlowTypes/flowTypes";
import { normalizeContextPath, generateEdge } from "../../../../helpers/common";
import { CanvasSkeleton } from "./skeleton";
import { createResourceDependencies, deleteResourceDependencies } from "redux/actions/portal/resourceDependenciesActionCreator";
import { extractDependenciesFromFlowConfig, getResourceDependencyContextId } from "helpers/resourceDependencies";

export const FlowCanvas = ({ zoomOnScroll, tabId, node, ref }) => {
   const { autoSaveFlow, toggleRedo, toggleUndo } = useContext(TabContextApi);
   const [nodes, setNodes, onNodesChange] = useNodesState([]);
   const [edges, setEdges, onEdgesChange] = useEdgesState([]);
   const [reactFlowInstance, setReactFlowInstance] = useState(null);
   const { flow, currentSavedFlow, reloadFlowCount, draggedNode, isChangingFlowMode } = useSelector((store) => store?.designer?.flows);
   const { checkIfTriggerDuplicate } = useCheckTriggersDuplicate();
   const dispatch = useDispatch();
   const reactFlowInstanceRef = useRef(null);
   const { takeSnapshot, canUndo, canRedo, undo, redo, isCanvasActiveRef } = useUndoRedo(() => {
      autoSaveFlow(reactFlowInstanceRef.current);
   });
   const { generateNodeId } = useComponentIdGenerator();
   const { tryToAddStepToCanvas, tryToGenerateNodeData, moveNode, willCreateCycleWithBranchCheck } = useFlowStepConnection();
   const { checkIsAllowAddNode } = useLimitNumberOfNode();
   const { flowTriggers } = useSelector((store) => store?.designer?.triggers);
   const { fitView, setViewport, getViewport, setCenter, zoomTo } = useReactFlow();
   const lastTargetRef = useRef({
      targetNode: null,
      isAllowedToDrop: false
   });
   const { getMergeNodeId } = useGetBranchNodes();
   const [timeNodeDragStart, setTimeNodeDragStart] = useState(0);
   const { viewOptions, isVerticalCanvasDirection } = useSelector((store) => store?.designer?.settings);
   const [isParsingFlow, setIsParsingFlow] = useState(false);
   const { user } = useAuth0();
   const { updateNodesMissingConnectors } = useDetermineMissingConnectors();
   const { createFloatingEdge } = useHandleFloatingEdge();
   const nodesInitialized = useNodesInitialized(options);
   const hasCentered = useRef(false);
   const { updateLayout } = useAutoLayout({
      direction: isVerticalCanvasDirection ? CANVAS_DIRECTIONS.VERTICAL : CANVAS_DIRECTIONS.HORIZONTAL
   });
   const reactFlowStore = useStoreApi();
   useAutoPanOnDrag();
   const [isCanvasReady, setIsCanvasReady] = useState(false);
   const { recentlyDeletedBranchNodeId } = useSelector((store) => store?.designer?.componentPropsPanel);
   const isMouseInsideRef = useRef(false);

   const onCanvasMouseEnter = () => {
      isMouseInsideRef.current = true;
   };
   const onCanvasMouseLeave = () => {
      isMouseInsideRef.current = false;
      isCanvasActiveRef.current = false;
   };
   const onCanvasPaneClick = () => {
      if (isMouseInsideRef.current) isCanvasActiveRef.current = true;
   };
   const onCanvasMoveEnd = () => {
      if (isMouseInsideRef.current) isCanvasActiveRef.current = true;
   };

   useImperativeHandle(ref, () => ({
      undo,
      redo
   }));

   useOnSelectionChange({
      onChange: ({ nodes }) => {
         if (nodes.length) return;

         dispatch(hideComponentPropsPanel());

         dispatch(clearSelectedLogNodeId());
      }
   });

   const getFlowCoordinates = (x, y) => {
      return reactFlowInstance.screenToFlowPosition({ x, y });
   };

   const createConnection = async (event) => {
      const newSourceId = event.target.closest(".react-flow__node")?.getAttribute("data-id") || event.target.closest(".edgebutton")?.getAttribute("data-source");
      let sourceId = event.target.closest(".edgebutton")?.getAttribute("data-target");

      if (!newSourceId) return;

      try {
         const parsed = JSON.parse(event.dataTransfer.getData("application/reactflow"));

         const sourceNode = reactFlowInstance.getNode(newSourceId);
         const position = getFlowCoordinates(event.clientX, event.clientY);
         let { nodeData, nodeType, isClone, draggedNode } = parsed;
         const nodeDataCopy = { ...nodeData, draggedNode }; //copy of node data because of inserting a cloned node

         if (draggedNode) {
            takeSnapshot();
            moveNode(nodeData, newSourceId, sourceId);
            autoSaveFlow(reactFlowInstance);
            return;
         }

         if (!isClone && !checkIsAllowAddNode(nodes)) {
            const activeDropAreaEl = document.querySelector(".active-drop-area");
            removeElementClassname(activeDropAreaEl, "active-drop-area");
            return;
         }

         const updatedNodeData = await tryToGenerateNodeData(nodeData, sourceNode.data);

         if (!sourceId) {
            sourceId = getOutgoers(sourceNode, nodes, edges)[0]?.id;
         }

         if (isEmptyValue(updatedNodeData)) {
            return;
         }

         const newNode = {
            position,
            id: isClone || draggedNode ? nodeDataCopy.id : generateNodeId(),
            type: nodeType,
            data: {
               ...updatedNodeData,
               componentKey: isClone || draggedNode ? nodeDataCopy.componentKey : updatedNodeData.componentKey,
               flowElement: isClone || draggedNode ? { ...nodeDataCopy.flowElement } : { ...updatedNodeData.flowElement },
               isDraggedNode: isClone || draggedNode
            },
            style: { opacity: 0 }
         };

         takeSnapshot();
         tryToAddStepToCanvas(newSourceId, sourceId, newNode);

         if (currentSavedFlow?.id) {
            const dependencies = extractDependenciesFromFlowConfig(currentSavedFlow.id, { nodes: [newNode], triggers: [] });

            if (dependencies.length) {
               dispatch(
                  createResourceDependencies({
                     tenantId: currentSavedFlow.tenant_id,
                     dependencies
                  })
               );
            }

            autoSaveFlow(reactFlowInstance);
         }
      } catch (err) {
         console.log("Error in createConnection: ", err);
      }
   };

   const onConnect = useCallback(
      (params) => {
         if (params.source === params.target) {
            return;
         }

         const node = reactFlowInstance.getNode(params.source);

         takeSnapshot();
         node.data.hasChildren = true;
         setNodes((nds) =>
            nds.map((node) => {
               if (node.id === params.source) {
                  node.data.hasChildren = true;
               }

               return node;
            })
         );
         setEdges((eds) => addEdge(eds));
      },
      [takeSnapshot, reactFlowInstance]
   );

   const onInit = (flowInstance) => {
      setReactFlowInstance(flowInstance);
      reactFlowInstanceRef.current = flowInstance;
   };

   const onDrop = useCallback(
      (event) => {
         event.preventDefault();

         if (currentSavedFlow?.shared_status === "active" || currentSavedFlow?.shared_status === "inactive") {
            return;
         }

         if (event.dataTransfer.getData("movedTrigger")) {
            const trigger = JSON.parse(event?.dataTransfer?.getData("movedTrigger"));

            if (checkIfTriggerDuplicate(trigger.type)) {
               return;
            }

            dispatch(addFlowTrigger(trigger));
            dispatch(addContextTrigger(trigger.label, currentSavedFlow?.name));

            if (currentSavedFlow?.id) {
               autoSaveFlow(reactFlowInstance);
            }
            return;
         }

         if (event.target instanceof Element) {
            createConnection(event);
         }
      },
      [reactFlowInstance, flow, currentSavedFlow, takeSnapshot, fitView, nodes, edges, flowTriggers]
   );

   const allowDropLocation = (newSourceId, sourceId, nodeDataCopy) => {
      const result = willCreateCycleWithBranchCheck(nodeDataCopy.id, newSourceId);
      return nodeDataCopy.id !== newSourceId && sourceId !== undefined && sourceId !== nodeDataCopy.id && !result;
   };

   const onDragOver = useCallback(
      (event) => {
         event.preventDefault();
         event.dataTransfer.dropEffect = "move";

         if (!draggedNode) return;

         const edgeButton = event.target.closest(".edgebutton");
         const dataSource = edgeButton?.getAttribute("data-source");
         const dataTarget = edgeButton?.getAttribute("data-target");

         if (!edgeButton) {
            event.dataTransfer.dropEffect = "none";
            if (lastTargetRef.current.targetNode) {
               lastTargetRef.current.targetNode.classList.remove("active-drop-area");
               lastTargetRef.current = {
                  targetNode: null,
                  isAllowedToDrop: false
               };
            }
            return;
         }

         if (lastTargetRef.current.targetNode === edgeButton) {
            event.dataTransfer.dropEffect = lastTargetRef.current.isAllowedToDrop ? "move" : "none";
            return;
         }

         const allowDrop = allowDropLocation(dataSource, dataTarget, draggedNode.nodeData);

         if (lastTargetRef.current.targetNode) {
            lastTargetRef.current.targetNode.classList.remove("active-drop-area");
         }

         if (allowDrop) {
            edgeButton.classList.add("active-drop-area");
         } else {
            edgeButton.classList.remove("active-drop-area");
         }

         lastTargetRef.current = {
            targetNode: edgeButton,
            isAllowedToDrop: allowDrop
         };

         event.dataTransfer.dropEffect = allowDrop ? "move" : "none";
      },
      [draggedNode]
   );

   const onConnectEnd = () => {
      autoSaveFlow(reactFlowInstance);
   };

   const onNodesDelete = useCallback(
      (deleted) => {
         let filteredEdges = edges.filter((edge) => edge.type !== EDGE_TYPES.FLOATING);
         const controlFlowEdges = filteredEdges.filter((edge) => edge.type !== EDGE_TYPES.STRAIGHT);
         const getNodeIncomers = (node) => getIncomers(node, nodes, controlFlowEdges);
         const getConnectedNodeEdges = (nds) => getConnectedEdges(nds, filteredEdges);
         const node = deleted[0];
         const incomers = getNodeIncomers(node);
         let connectedEdges = [],
            remainingEdges = [],
            createdEdges = [];
         let subFlowNodesToDelete = [];

         if (deleted.length > 1) {
            const nodeToUse = recentlyDeletedBranchNodeId ? reactFlowInstance.getNode(recentlyDeletedBranchNodeId) : node;

            const mergeId = getMergeNodeId(nodeToUse);
            const mergeNode = reactFlowInstance.getNode(mergeId);
            let parentNode = reactFlowInstance.getNode(mergeNode?.data?.parentNodeId);
            let parentNodeIncomers = null;
            let mergeNodeOutgoers = null;
            if (mergeNode) {
               parentNodeIncomers = getNodeIncomers(parentNode);
               mergeNodeOutgoers = getOutgoers(mergeNode, nodes, filteredEdges);
            }

            connectedEdges = getConnectedNodeEdges(deleted);
            remainingEdges = filteredEdges.filter((edge) => !connectedEdges.includes(edge));
            if (nodeToUse.type !== NODE_TYPES.LABEL) {
               createdEdges.push(generateEdge(parentNodeIncomers[0]?.id, mergeNodeOutgoers[0]?.id, EDGE_TYPES.BUTTON));
            }

            deleted.forEach((deletedNode) => {
               if (deletedNode.data?.type === COMMON_COMPONENT_TYPES.INVOKE) {
                  const subFlowNode = nodes.find((n) => n.data?.parentNodeId === deletedNode.id);
                  if (subFlowNode) {
                     subFlowNodesToDelete.push(subFlowNode);
                  }
               }
            });

            setNodes((nodes) => nodes.filter((n) => ![...deleted, ...subFlowNodesToDelete].map((dn) => dn.id).includes(n.id)));
            dispatch(setRecentlyDeletedBranchNodeId(null));
         } else {
            // create new edges from incomers to outgoers
            const outgoers = getOutgoers(node, nodes, controlFlowEdges);
            remainingEdges = filteredEdges.filter((edge) => edge.target !== node.id && edge.source !== node.id);
            createdEdges.push(generateEdge(incomers[0]?.id, outgoers[0]?.id, EDGE_TYPES.BUTTON));

            if (node.data?.type === COMMON_COMPONENT_TYPES.INVOKE) {
               // remove the invoke subflow node
               const subFlowNode = nodes.find((n) => n.data?.parentNodeId === node.id);
               if (subFlowNode) {
                  subFlowNodesToDelete.push(subFlowNode);
                  setNodes((nodes) => nodes.filter((n) => n.id !== subFlowNode.id && n.id !== deleted[0].id));
               }
            }
         }

         const deletedNodeIds = new Set([...deleted, ...subFlowNodesToDelete].map((deletedNode) => deletedNode.id));
         const remainingNodeIds = new Set(nodes.filter((existingNode) => !deletedNodeIds.has(existingNode.id)).map((existingNode) => existingNode.id));
         const remainingFloatingEdges = edges.filter(
            (edge) => edge.type === EDGE_TYPES.FLOATING && !deletedNodeIds.has(edge.source) && !deletedNodeIds.has(edge.target)
         );
         const updatedEdges = [...remainingEdges, ...createdEdges, ...remainingFloatingEdges].filter(
            (edge) => remainingNodeIds.has(edge.source) && remainingNodeIds.has(edge.target)
         );
         const uniqueEdges = [...new Map(updatedEdges.map((edge) => [edge.id, edge])).values()];

         setEdges(uniqueEdges);
         takeSnapshot();

         deleted.forEach((deletedNode) => {
            const { componentKey, type } = deletedNode.data || {};
            if (!componentKey) return;

            if (type === COMMON_COMPONENT_TYPES.VARIABLES) {
               dispatch(deleteContextVariable(componentKey, currentSavedFlow?.name));
            }
            dispatch(removeStepFromContext(componentKey, currentSavedFlow?.name));
         });

         if (node.type !== NODE_TYPES.LABEL) autoSaveFlow(reactFlowInstance);

         const deletedConnectorNodes = deleted.filter((deletedNode) => deletedNode.type === "custom" && deletedNode.data?.componentId);
         const deletedInvokeNodes = deleted.filter((deletedNode) => deletedNode.data?.type === COMMON_COMPONENT_TYPES.INVOKE);
         const currentCanvasNodes = reactFlowInstance.getNodes();

         const dependenciesToDelete = [];

         deletedInvokeNodes.forEach((deletedInvokeNode) => {
            const subFlowNode = currentCanvasNodes.find((n) => n.data?.parentNodeId === deletedInvokeNode.id);
            const subFlowId = subFlowNode?.data?.flowId;

            if (subFlowId) {
               dependenciesToDelete.push({
                  sourceId: currentSavedFlow.id,
                  targetId: subFlowId,
                  sourceResourceType: "flow",
                  targetResourceType: "flow",
                  contextId: getResourceDependencyContextId(currentSavedFlow.id, deletedInvokeNode.id)
               });
            }
         });

         deletedConnectorNodes.forEach((deletedConnectorNode) => {
            const connectorId = deletedConnectorNode.data.componentId;
            const credentialId = deletedConnectorNode.data?.flowElement?.data?.credential?.value;

            dependenciesToDelete.push({
               sourceId: currentSavedFlow.id,
               targetId: connectorId,
               sourceResourceType: "flow",
               targetResourceType: "connector",
               contextId: getResourceDependencyContextId(currentSavedFlow.id, deletedConnectorNode.id)
            });

            if (credentialId) {
               dependenciesToDelete.push({
                  sourceId: connectorId,
                  targetId: credentialId,
                  sourceResourceType: "connector",
                  targetResourceType: "credential",
                  contextId: getResourceDependencyContextId(currentSavedFlow.id, deletedConnectorNode.id)
               });
            }
         });

         deleted
            .filter((deletedNode) => !(deletedNode.type === "custom" && deletedNode.data?.componentId))
            .forEach((deletedNode) => {
               const credentialId = deletedNode.data?.flowElement?.data?.credential?.value;

               if (!credentialId) return;

               dependenciesToDelete.push({
                  sourceId: currentSavedFlow.id,
                  targetId: credentialId,
                  sourceResourceType: "flow",
                  targetResourceType: "credential",
                  contextId: getResourceDependencyContextId(currentSavedFlow.id, deletedNode.id)
               });
            });

         if (dependenciesToDelete.length > 0) {
            dispatch(
               deleteResourceDependencies({
                  tenantId: currentSavedFlow.tenant_id,
                  dependencies: dependenciesToDelete
               })
            );
         }
      },
      [reactFlowInstance, takeSnapshot, nodes, edges, recentlyDeletedBranchNodeId, currentSavedFlow]
   );

   const onEdgesDelete = useCallback(
      (edgs) => {
         const nds = reactFlowInstance.getNodes();
         const ndsCopy = [...nds];
         takeSnapshot();
         edgs.forEach((e) => {
            const node = ndsCopy.find((n) => n.id === e.source);
            const outgoers = getOutgoers(node, nodes, edges);
            if (outgoers.length == 1) {
               node.data.hasChildren = false;
            }
         });
         setNodes(ndsCopy);
         autoSaveFlow(reactFlowInstance);
      },
      [reactFlowInstance, edges, nodes, takeSnapshot]
   );

   const onNodeDragStart = useCallback(
      (event) => {
         event.preventDefault();
         takeSnapshot();
         setTimeNodeDragStart(new Date().getTime());
      },
      [takeSnapshot, timeNodeDragStart]
   );

   const onNodeDragStop = (event) => {
      event.preventDefault();
      const timeNodeDragStop = new Date().getTime();

      if (timeNodeDragStop - timeNodeDragStart > 400) {
         autoSaveFlow(reactFlowInstance);
      }
   };

   const onSelectionDragStart = useCallback(() => {
      takeSnapshot();
   }, [takeSnapshot]);

   const checkCredentials = async (nodes, triggers) => {
      const credentialsInFLow = [];
      let hasCredentials = false;

      if (triggers) {
         for (const trigger of triggers) {
            if (trigger.properties?.credentials && trigger.properties?.credentials?.required) {
               const crName = trigger.properties?.credentials?.name;
               const crType = trigger.properties?.credentials?.credentialType;
               credentialsInFLow.push({ name: crName, type: crType });
               hasCredentials = true;
               break;
            }
         }
      }

      if (nodes) {
         for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];

            if (node.data?.flowElement?.data && node.data.flowElement.data.credential) {
               const res = await getCredentialById(user?.stripeId, node.data.flowElement.data.credential.value);
               if (res?.resultObject) {
                  const crName = res?.resultObject.application?.name;
                  const crType = res?.resultObject.application?.applicationType;
                  credentialsInFLow.push({ name: crName, type: crType });
               }
               hasCredentials = true;
               break;
            }
         }
      }

      dispatch(setCredentialsInSharedFlow(credentialsInFLow));

      return hasCredentials;
   };

   const getContextValue = (fieldValue, context) => {
      if (!fieldValue || typeof fieldValue !== "string") return undefined;

      if (!fieldValue.startsWith("@")) return fieldValue;

      const path = normalizeContextPath(fieldValue);

      return path.split(".").reduce((obj, key) => {
         if (obj && obj[key] !== undefined) {
            return obj[key];
         }
         return undefined;
      }, context);
   };

   const parseFlow = async (flowToParse) => {
      setIsCanvasReady(false); // 👈 ADD THIS
      setIsParsingFlow(true);
      dispatch(clearFlowTriggers());
      flowToParse.steps = flowToParse.steps.replace(/,"http"/g, ",http").replace(/,"https"/g, ",https");

      hasCentered.current = false;

      const { nodes, edges, context, triggers = [] } = JSON.parse(flowToParse.steps);
      const isOwner = flowToParse.created_by_id === user?.cxfUserId;

      if (!isOwner && checkCredentials(nodes, triggers)) {
         dispatch(openShareeFlowDialog());
      }

      const parsedIntegrations = flowToParse?.integrations ? JSON.parse(flowToParse.integrations) : [];

      if (context) {
         let cleanedContext = contextCleanUp(context, nodes, user.stripeId);

         if (!context.flow_name) {
            cleanedContext.flow_name = flowToParse.name;
         }

         if (triggers?.length > 0) {
            const { adjustedTriggers, adjustedContext } = adjustTriggerCredentials(triggers, cleanedContext);
            cleanedContext = adjustedContext;
            dispatch(setFlowTriggers(adjustedTriggers));
         }

         cleanedContext.integration_ids = parsedIntegrations;
         cleanedContext.session = null;

         dispatch(setContext(cleanedContext));
      } else {
         dispatch(
            setContext({
               settings: {},
               steps: {},
               variables: {},
               triggers: {},
               session: null,
               tenant_id: user.stripeId,
               flow_id: flowToParse.id,
               integration_ids: parsedIntegrations
            })
         );
      }

      if (nodes.length > 2) {
         for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];

            if (node.data?.configFiles && !node.data.componentFiles) {
               const response = await getConnectorConfigFile(getSchemaUrlFromConfigFiles(node.data.configFiles));
               node.data.componentFiles = {
                  schema: response.resultObject.data
               };
            }

            node.data.expanded = true;
            node.selected = false;
            node.className = "nopan";
            if (node.data?.type !== COMMON_COMPONENT_TYPES.VARIABLES) continue;

            const actionForm = node.data?.flowElement?.data;

            if (actionForm && Object.keys(actionForm).length) {
               const varsData = [];

               for (const key in actionForm) {
                  if (!key.startsWith("var_") && !key.startsWith("reference_var_")) continue;

                  const variable = actionForm[key];
                  let defaultValue = variable?.defaultInput?.value;

                  defaultValue = getContextValue(defaultValue, store.getState().designer.editorContext.context) ?? defaultValue;

                  if (!variable?.value) continue;
                  varsData.push({
                     variableName: variable.value,
                     variableValue: key.startsWith("reference_var_") ? null : defaultValue
                  });
               }

               dispatch(updateContextVariable(node.data?.componentKey, varsData, flowToParse.name));
            }
         }
         const updatedEdges = [...edges];

         const updatedNodes = await updateNodesMissingConnectors(nodes);
         setNodes(updatedNodes);

         if (edges.length) {
            nodes.forEach((node) => {
               if (node.data.type !== COMMON_COMPONENT_TYPES.GOTO) return;

               const targetNodeId = node.data?.flowElement?.data?.nodeId?.value;

               if (targetNodeId) {
                  updatedEdges.push(createFloatingEdge(node.id, targetNodeId));
               }
            });

            setEdges(updatedEdges);
         }
      } else {
         setNodes(initialNodes);
         setEdges(initialEdges);
      }

      setIsParsingFlow(false);

      setTimeout(() => {
         saveFlowInstance();
         dispatch(setFlowIsLoading(false));
      }, 1000);
   };

   const reloadCurrentFlow = async () => {
      try {
         if (currentSavedFlow?.id) {
            const flowToParse = await fetchFlowById(user?.stripeId, currentSavedFlow.id);
            if (flowToParse) {
               parseFlow(flowToParse);
            }
         }
      } catch (error) {
         console.error(error);
      }
   };

   const saveFlowInstance = () => {
      const flowObject = reactFlowInstance?.toObject();

      if (flowObject) {
         {
            dispatch(setCanvasFlowInstance(flowObject));
         }
      }
   };

   const doFitView = (zoomValue) => {
      const options = { duration: 400 };

      if (zoomValue) {
         options.minZoom = zoomValue || viewOptions.zoomLevel;
         options.maxZoom = zoomValue || viewOptions.zoomLevel;
      }

      fitView(options);
   };

   useEffect(() => {
      if (!reactFlowInstance) return;

      if (tabId) {
         saveFlowInstance();
      }

      if (currentSavedFlow) {
         parseFlow(currentSavedFlow);
         return;
      }

      setNodes(initialNodes);
      setEdges(initialEdges);
   }, [reactFlowInstance]);

   useEffect(() => {
      if (reloadFlowCount > 0) {
         reloadCurrentFlow();
      }
   }, [reloadFlowCount]);

   useEffect(() => {
      if ((!nodesInitialized && !isChangingFlowMode) || hasCentered.current) {
         setViewport(getViewport());

         return;
      }

      updateLayout();

      const startNode = nodes.find((node) => node.id === "1");

      if (startNode) {
         const zoom = viewOptions.zoomLevel ?? 1;
         const x = startNode.position.x;
         const y = startNode.position.y;
         const { height } = reactFlowStore.getState();
         const offsetY = height / 2 / zoom;
         setCenter(x, y + offsetY, { zoom, duration: 400 });

         if (isChangingFlowMode) {
            dispatch(setIsChangingFlowMode(false));
         }

         hasCentered.current = true;
      }
   }, [nodesInitialized, reactFlowInstance]);

   useEffect(() => {
      let timeout;
      if (viewOptions.zoomLevel) {
         timeout = setTimeout(() => doFitView(viewOptions.zoomLevel), 400);
         return () => clearTimeout(timeout);
      } else {
         doFitView(1);
      }
   }, [isVerticalCanvasDirection]);

   useEffect(() => {
      if (!currentSavedFlow) return;

      dispatch(fetchSharedFlowConfigurations());

      if (!currentSavedFlow.steps) return;

      try {
         const parsedSteps = JSON.parse(currentSavedFlow.steps);
         const { triggers } = parsedSteps;
         if (triggers?.length > 0) {
            dispatch(setFlowTriggers(triggers));
         }

         // fetch flow configuration
         dispatch(fetchFlowConfiguration());
      } catch (e) {
         console.error("Error in parsing current saved flow", e);
      }
   }, [currentSavedFlow]);

   useEffect(() => {
      zoomTo(viewOptions.zoomLevel ?? 1, { duration: 400 });
   }, [viewOptions.zoomLevel]);

   useEffect(() => {
      if (!currentSavedFlow?.current_version) return;

      setIsCanvasReady(false);

      let frame1;
      let frame2;

      const unsubscribe = reactFlowStore.subscribe(() => {
         const { nodeInternals } = reactFlowStore.getState();

         if (nodeInternals.size > 0) {
            frame1 = requestAnimationFrame(() => {
               frame2 = requestAnimationFrame(() => {
                  setIsCanvasReady(true);
               });
            });
         }
      });

      return () => {
         unsubscribe();
         cancelAnimationFrame(frame1);
         cancelAnimationFrame(frame2);
      };
   }, [currentSavedFlow?.current_version, reactFlowStore]);

   useEffect(() => {
      node.setEventListener("close", () => {
         window.history.replaceState(null, "CXFabric", "/designer");
      });
   }, []);

   useEffect(() => {
      toggleRedo(!canRedo);
   }, [canRedo]);

   useEffect(() => {
      toggleUndo(!canUndo);
   }, [canUndo]);

   return (
      <>
         {(isParsingFlow || !isCanvasReady) && (
            <CircularProgressWrapper>
               <CanvasSkeleton />
            </CircularProgressWrapper>
         )}
         <FlowStateContextApi.Provider value={{ takeSnapshot }}>
            <ReactFlow
               key={currentSavedFlow?.current_version}
               id={currentSavedFlow.id}
               className={`reactflow ${isCanvasReady ? "rf-ready" : "rf-loading"}`}
               nodesDraggable={false}
               nodes={nodes}
               edges={edges}
               edgeTypes={edgeTypes}
               onNodesChange={onNodesChange}
               onEdgesChange={onEdgesChange}
               onConnect={onConnect}
               onInit={onInit}
               onDrop={onDrop}
               onDragOver={onDragOver}
               onMoveStart={() => { }}
               onMoveEnd={onCanvasMoveEnd}
               onPaneClick={onCanvasPaneClick}
               onMouseEnter={onCanvasMouseEnter}
               onMouseLeave={onCanvasMouseLeave}
               nodeTypes={nodeTypes}
               proOptions={{
                  hideAttribution: true
               }}
               onConnectEnd={onConnectEnd}
               onNodesDelete={onNodesDelete}
               onEdgesDelete={onEdgesDelete}
               onNodeDragStart={onNodeDragStart}
               onNodeDragStop={onNodeDragStop}
               onSelectionDragStart={onSelectionDragStart}
               defaultEdgeOptions={defaultEdgeOptions}
               zoomOnScroll={zoomOnScroll}
               panOnDrag
               connectionLineStyle={{
                  strokeWidth: 1.5
               }}
               elementsSelectable={currentSavedFlow?.shared_status === "active" || currentSavedFlow?.shared_status !== "active"}
               zoomOnDoubleClick
            >
               <FlowControls />
               {viewOptions.showControls && viewOptions.showMiniMap && <MiniMap pannable zoomable onClick={(event, position) => setCenter(position.x, position.y, { zoom: 1 })} ariaLabel="" />}
               {currentSavedFlow?.shared_status === "active" || currentSavedFlow?.shared_status === "inactive" ? <ModeControl /> : null}
            </ReactFlow>
         </FlowStateContextApi.Provider>
      </>
   );
};

const options = {
   includeHiddenNodes: false
};
